import axios, { AxiosError } from 'axios';

import config from 'config';
import { ChatbotInitResponse, ChatbotStreamEvent } from './types';
import { http } from 'services';

const streamHeaders: HeadersInit = {
  Accept: 'text/event-stream',
  'Cache-Control': 'no-cache',
  'Content-Type': 'application/json'
};

function parseSseEventBlock(block: string): ChatbotStreamEvent | null {
  const lines = block.split('\n').map(l => l.trimEnd());
  const dataLines = lines
    .filter(l => l.startsWith('data:'))
    .map(l => l.replace(/^data:\s?/, ''))
    .filter(Boolean);

  if (dataLines.length === 0) return null;

  try {
    return JSON.parse(dataLines.join('\n')) as ChatbotStreamEvent;
  } catch {
    return null;
  }
}

function consumeSseBuffer(
  buffer: string,
  handlers: {
    onDone: (finalText: string) => void;
  }
): { rest: string; finished: boolean } {
  let rest = buffer.replace(/\r\n/g, '\n');
  let finished = false;

  let idx = rest.indexOf('\n\n');
  while (idx !== -1) {
    const block = rest.slice(0, idx).trim();
    rest = rest.slice(idx + 2);
    idx = rest.indexOf('\n\n');

    if (!block) continue;

    const evt = parseSseEventBlock(block);
    if (!evt) continue;

    if (evt.type === 'done' && typeof evt.content === 'string') {
      handlers.onDone(evt.content);
      finished = true;
      break;
    }
  }

  return { rest, finished };
}

function axiosConfigWithSignal(signal?: AbortSignal) {
  if (!signal) return {};
  const source = axios.CancelToken.source();
  signal.addEventListener('abort', () => source.cancel('aborted'));
  return { cancelToken: source.token };
}

/** Oddiy GET — chatbot config (JSON). */
export async function initChatbot(signal?: AbortSignal) {
  try {
    const { data: json } = await http.request.get<ChatbotInitResponse>("/notification/chatbot/init", {
      ...axiosConfigWithSignal(signal),
      headers: {
        Accept: 'application/json'
      }
    });

    if (json?.status_code !== 0 || !json?.data?.title) {
      throw new Error(json?.status_message || 'Chatbot init returned non-success');
    }

    return json.data;
  } catch (err) {
    const axiosErr = err as AxiosError<ChatbotInitResponse>;
    const message =
      axiosErr.response?.data?.status_message ||
      axiosErr.message ||
      'Chatbot init failed';
    throw new Error(message);
  }
}

/**
 * SSE stream (POST).
 * Browser’da axios `responseType: 'stream'` ishlamaydi (faqat Node).
 * Shuning uchun fetch + ReadableStream.getReader() — useSSE misolidagi kabi.
 */
export async function streamChatbot(params: {
  prompt: string;
  thread_id: string;
  signal?: AbortSignal;
  onDone: (finalText: string) => void;
}) {
  const { prompt, thread_id, signal, onDone } = params;
  const handlers = { onDone };

  const auth = http.request.defaults.headers['Authorization'];
  const headers: HeadersInit = {
    ...streamHeaders,
    ...(auth ? { Authorization: String(auth) } : {})
  };

  const response = await fetch(`${config.api.base_url}/notification/chatbot/stream`, {
    method: 'POST',
    signal,
    headers,
    body: JSON.stringify({ prompt, thread_id })
  });

  if (!response.ok) {
    throw new Error(`Chatbot stream failed (${response.status})`);
  }

  if (!response.body) {
    throw new Error('Chatbot stream: empty response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');

  let buffer = '';
  let finished = false;

  try {
    while (!finished) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const result = consumeSseBuffer(buffer, handlers);
      buffer = result.rest;
      finished = result.finished;
    }

    if (!finished && buffer) {
      const padded = buffer.endsWith('\n\n') ? buffer : `${buffer}\n\n`;
      consumeSseBuffer(padded, handlers);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}
